import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSessionPolicy, refreshSession } from '../api/session';

// Seconds of remaining idle time at which the user is warned before being
// signed out, so a dispatcher can keep the session with one keystroke.
const WARN_SECONDS = 60;
// How often the guard evaluates idle time / token freshness.
const TICK_MS = 5_000;
// Renew the access token this long before it expires so a request never has to
// fail with a 401 first.
const RENEW_BEFORE_MS = 120_000;
// Activity is shared through localStorage so working in a popped-out window
// (Map/Units/Chat) counts as activity for every other pane.
const ACTIVITY_KEY = 'last_activity';
const ACTIVITY_WRITE_MS = 10_000;

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousedown',
  'keydown',
  'wheel',
  'touchstart',
];

function tokenExpiryMs(token: string): number | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function readActivity(): number {
  const raw = Number(localStorage.getItem(ACTIVITY_KEY));
  return Number.isFinite(raw) && raw > 0 ? raw : Date.now();
}

export interface SessionGuard {
  /** Seconds until automatic sign-out, while the warning is showing. */
  idleCountdown: number | null;
  /** Dismiss the warning and treat the session as active again. */
  keepAlive: () => void;
}

/**
 * Keeps an authenticated session alive while the user is working and signs
 * them out after the tenant's configured inactivity window.
 *
 * Dispatch shifts run 8/10/12 hours, so the access token is renewed silently
 * from the refresh token instead of expiring mid-shift. Inactivity — not
 * elapsed time — is what ends a session (unless the tenant also sets an
 * absolute cap, which the server enforces on refresh).
 */
export function useSessionGuard(enabled: boolean, isMdt: boolean, onExpire: () => void): SessionGuard {
  const [idleMinutes, setIdleMinutes] = useState(0);
  const [idleCountdown, setIdleCountdown] = useState<number | null>(null);
  const lastWrite = useRef(0);
  const expiredRef = useRef(false);

  const markActive = useCallback(() => {
    const now = Date.now();
    if (now - lastWrite.current < ACTIVITY_WRITE_MS) return;
    lastWrite.current = now;
    localStorage.setItem(ACTIVITY_KEY, String(now));
  }, []);

  const keepAlive = useCallback(() => {
    lastWrite.current = 0;
    markActive();
    setIdleCountdown(null);
    void refreshSession();
  }, [markActive]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void fetchSessionPolicy().then((policy) => {
      if (cancelled || !policy) return;
      setIdleMinutes(isMdt ? policy.mdt_idle_timeout_minutes : policy.dispatch_idle_timeout_minutes);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, isMdt]);

  useEffect(() => {
    if (!enabled) return;
    if (!localStorage.getItem(ACTIVITY_KEY)) markActive();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActive));
    };
  }, [enabled, markActive]);

  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      if (idleMinutes > 0) {
        const windowMs = idleMinutes * 60_000;
        // On a very short window the warning would otherwise cover the whole
        // window and show up the moment the user signs in.
        const warnAt = Math.min(WARN_SECONDS * 1000, windowMs / 2);
        const remaining = windowMs - (Date.now() - readActivity());
        if (remaining <= 0) {
          if (expiredRef.current) return;
          expiredRef.current = true;
          onExpire();
          return;
        }
        setIdleCountdown(remaining <= warnAt ? Math.ceil(remaining / 1000) : null);
        // Don't renew a session that is about to be signed out anyway.
        if (remaining <= warnAt) return;
      }

      const expiry = tokenExpiryMs(token);
      if (expiry !== null && expiry - Date.now() <= RENEW_BEFORE_MS) {
        void refreshSession();
      }
    };
    tick();
    const timer = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(timer);
  }, [enabled, idleMinutes, onExpire]);

  return { idleCountdown, keepAlive };
}
